import { useState } from "react";

/**
 * AccountPage
 *
 * The customer's own account: whether the address is verified, the password, and
 * the second factor. It is deliberately a settings screen rather than part of
 * onboarding — the verification banner sends people here, and everything else on
 * it is optional.
 *
 * Props:
 *   user — { email, email_verified, phone, phone_verified, two_factor_enabled,
 *            two_factor_method }
 *   onResendVerification()
 *   onChangePassword({ currentPassword, newPassword }) — resolves on success
 *   onEnableTotp() — resolves with { secret, otpauth_url, qr_image }
 *                      (`qr_image` is a PNG data URI the screen renders as the
 *                      scannable QR; `secret` remains for manual entry)
 *   onEnableSms(phone) — resolves with { destination }
 *   onConfirmTwoFactor(code) — completes whichever factor is pending
 *   onRequestDisableCode() — sends the code an SMS factor needs to be turned off
 *   onDisableTwoFactor(code)
 *   message — { tone, text } shown above the sections
 */
export default function AccountPage({
  user,
  onResendVerification,
  onChangePassword,
  onChangeTimezone,
  onEnableTotp,
  onEnableSms,
  onConfirmTwoFactor,
  onRequestDisableCode,
  onDisableTwoFactor,
  message,
}) {
  const verified = Boolean(user?.email_verified);
  const method = user?.two_factor_method ?? "";
  const enabled = Boolean(user?.two_factor_enabled) || method !== "";
  // The role can be granted before a factor exists (the admin command line
  // grants it, and the account signs in normally). Admin access stays blocked
  // until one is enrolled, so the screen says exactly that instead of letting
  // the operator wonder why /admin refused them.
  const needsAdminFactor = user?.role === "admin" && !user?.can_admin;

  // Which step of the two-factor flow is on screen. One piece of state because
  // only one of them can be in progress.
  const [step, setStep] = useState("idle"); // idle | totp | sms | disabling
  // The whole enrolment answer: the secret for manual entry and the QR image the
  // authenticator app scans. Both come from the server, which is also where the
  // QR is rendered, because its payload is the secret itself.
  const [enrolment, setEnrolment] = useState(null);
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(null);

  async function run(action, onDone) {
    setBusy(true);
    setLocal(null);
    try {
      await action();
      onDone?.();
    } catch (error) {
      setLocal({ tone: "danger", text: error?.message || "That did not work. Try again." });
    } finally {
      setBusy(false);
    }
  }

  const banner = message ?? local;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 640 }}>
      <div>
        <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-lg)" }}>Account</p>
        <p style={{ margin: "2px 0 0", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          {user?.email}
        </p>
      </div>

      {banner ? (
        <p
          className="ical-badge"
          data-tone={banner.tone}
          style={{
            background: banner.tone === "danger" ? "var(--bg-danger)" : "var(--bg-success)",
            color: banner.tone === "danger" ? "var(--text-danger)" : "var(--text-success)",
            padding: "8px 10px",
            display: "block",
          }}
        >
          {banner.text}
        </p>
      ) : null}

      {needsAdminFactor ? (
        <p
          data-testid="admin-factor-notice"
          className="ical-badge"
          style={{
            background: "var(--bg-warning, var(--surface-0))",
            color: "var(--text-warning)",
            padding: "8px 10px",
            display: "block",
          }}
        >
          This account is an administrator, but admin access is blocked until a second factor is
          set up. Turn one on below; the admin area opens on the next page load.
        </p>
      ) : null}

      <section className="ical-card" aria-label="Email">
        <p style={{ margin: "0 0 4px", fontWeight: 500 }}>Email address</p>
        {verified ? (
          <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
            Verified. This is where verification codes and password resets are sent.
          </p>
        ) : (
          <>
            <p style={{ margin: "0 0 10px", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
              Not verified yet. You can sign in, but creating a project or adding a calendar is
              blocked until you confirm this address.
            </p>
            <button
              type="button"
              className="ical-btn ical-btn-primary"
              disabled={busy}
              onClick={() => run(onResendVerification)}
            >
              Send me a new code
            </button>
          </>
        )}
      </section>

      <TimezoneSection
        timezone={user?.display_timezone}
        busy={busy}
        onChange={(timezone) => run(() => onChangeTimezone?.(timezone))}
      />

      <PasswordSection busy={busy} onSubmit={onChangePassword} run={run} />

      <section className="ical-card" aria-label="Two-factor authentication">
        <p style={{ margin: "0 0 4px", fontWeight: 500 }}>Two-factor authentication</p>
        <p style={{ margin: "0 0 12px", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          {!enabled
            ? "A code from an authenticator app, or a code we text you, on top of your password."
            : method === "sms"
            ? `A code is texted to ${user?.phone || "your number"} at every sign-in.`
            : "A code from your authenticator app is required at every sign-in."}
        </p>

        {!enabled && step === "idle" ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="ical-btn ical-btn-primary"
              disabled={busy}
              onClick={() =>
                // The secret and the QR both have to be on screen before the app
                // can be enrolled, so the response is kept rather than discarded.
                run(
                  async () => setEnrolment((await onEnableTotp()) ?? null),
                  () => setStep("totp"),
                )
              }
            >
              Use an authenticator app
            </button>
            {onEnableSms ? (
              // No code is sent yet: the number has to be typed first, and that
              // is the next step.
              <button type="button" className="ical-btn" disabled={busy} onClick={() => setStep("sms")}>
                Use SMS codes
              </button>
            ) : null}
          </div>
        ) : null}

        {step === "totp" ? (
          <TwoFactorEnrolment
            secret={enrolment?.secret}
            qrImage={enrolment?.qr_image}
            busy={busy}
            code={code}
            onCode={setCode}
            onCancel={() => {
              setStep("idle");
              setCode("");
            }}
            title="Scan this with your authenticator app"
            hint="Then enter the 6-digit code it shows."
            onConfirm={() =>
              run(
                () => onConfirmTwoFactor(code),
                () => {
                  setStep("idle");
                  setCode("");
                },
              )
            }
          />
        ) : null}

        {step === "sms" ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              run(
                () => onEnableSms(phone),
                () => setStep("sms-pending"),
              );
            }}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            <label htmlFor="account-phone" style={labelStyle}>
              Phone number (international format, e.g. +48500600700)
            </label>
            <input
              id="account-phone"
              className="ical-input"
              type="tel"
              required
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button type="submit" className="ical-btn ical-btn-primary" disabled={busy}>
                Send me a code
              </button>
              <button type="button" className="ical-btn" onClick={() => setStep("idle")} disabled={busy}>
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {step === "sms-pending" ? (
          <ConfirmationForm
            busy={busy}
            code={code}
            onCode={setCode}
            title="Enter the code we texted you"
            confirmLabel="Turn on SMS codes"
            onCancel={() => {
              setStep("idle");
              setCode("");
            }}
            onConfirm={() =>
              run(
                () => onConfirmTwoFactor(code),
                () => {
                  setStep("idle");
                  setCode("");
                },
              )
            }
          />
        ) : null}

        {enabled && step === "idle" ? (
          <button type="button" className="ical-btn ical-btn-danger" disabled={busy} onClick={() => setStep("disabling")}>
            Turn off two-factor
          </button>
        ) : null}

        {enabled && step === "disabling" ? (
          method === "sms" ? (
            <ConfirmationForm
              busy={busy}
              code={code}
              onCode={setCode}
              title="We will text you a code to confirm"
              sendLabel="Send me a code"
              onSend={() => run(onRequestDisableCode)}
              confirmLabel="Turn off two-factor"
              onCancel={() => {
                setStep("idle");
                setCode("");
              }}
              onConfirm={() =>
                run(
                  () => onDisableTwoFactor(code),
                  () => {
                    setStep("idle");
                    setCode("");
                  },
                )
              }
            />
          ) : (
            <ConfirmationForm
              busy={busy}
              code={code}
              onCode={setCode}
              title="Enter a code from your authenticator app"
              confirmLabel="Turn off two-factor"
              onCancel={() => {
                setStep("idle");
                setCode("");
              }}
              onConfirm={() =>
                run(
                  () => onDisableTwoFactor(code),
                  () => {
                    setStep("idle");
                    setCode("");
                  },
                )
              }
            />
          )
        ) : null}
      </section>
    </div>
  );
}

/** labelStyle is shared by the account screen's field labels. */
const labelStyle = { fontSize: "var(--text-xs)", color: "var(--text-secondary)" };

/** PasswordSection is the change-password form. */
function PasswordSection({ busy, onSubmit, run }) {
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew] = useState("");
  const [confirmPassword, setConfirm] = useState("");
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;

  return (
    <section className="ical-card" aria-label="Password">
      <p style={{ margin: "0 0 10px", fontWeight: 500 }}>Password</p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (mismatch) return;
          run(
            () => onSubmit({ currentPassword, newPassword }),
            () => {
              setCurrent("");
              setNew("");
              setConfirm("");
            },
          );
        }}
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
      >
        <label htmlFor="account-current-password" style={labelStyle}>
          Current password
        </label>
        <input
          id="account-current-password"
          className="ical-input"
          type="password"
          required
          autoComplete="current-password"
          value={currentPassword}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <label htmlFor="account-new-password" style={labelStyle}>
          New password
        </label>
        <input
          id="account-new-password"
          className="ical-input"
          type="password"
          required
          minLength={12}
          autoComplete="new-password"
          value={newPassword}
          onChange={(event) => setNew(event.target.value)}
        />
        <label htmlFor="account-confirm-password" style={labelStyle}>
          Repeat the new password
        </label>
        <input
          id="account-confirm-password"
          className="ical-input"
          type="password"
          required
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(event) => setConfirm(event.target.value)}
          style={{ borderColor: mismatch ? "var(--fill-danger)" : undefined }}
        />
        {mismatch ? (
          <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-danger)" }}>
            Passwords don't match.
          </p>
        ) : null}
        <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          Changing it signs out every other device. This one stays signed in.
        </p>
        <button type="submit" className="ical-btn ical-btn-primary" disabled={busy} style={{ alignSelf: "flex-start" }}>
          Change password
        </button>
      </form>
    </section>
  );
}

/** TwoFactorEnrolment shows the QR an authenticator scans, plus the secret. */
function TwoFactorEnrolment({ secret, qrImage, busy, code, onCode, onConfirm, onCancel, title, hint }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <p style={{ margin: 0, fontSize: "var(--text-sm)" }}>{title}</p>
      {qrImage ? (
        <img
          src={qrImage}
          alt="QR code that adds this account to an authenticator app"
          width={180}
          height={180}
          data-testid="totp-qr"
          style={{ alignSelf: "center", background: "#fff", padding: 8, borderRadius: "var(--radius)" }}
        />
      ) : null}
      <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
        {qrImage
          ? "Can't scan it? Add the account by entering this key instead:"
          : "Enter this key in your authenticator app:"}
      </p>
      <code
        data-testid="totp-secret"
        style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-sm)", wordBreak: "break-all" }}
      >
        {secret}
      </code>
      <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{hint}</p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onConfirm();
        }}
        style={{ display: "flex", gap: 8 }}
      >
        <input
          className="ical-input"
          inputMode="numeric"
          required
          placeholder="6-digit code"
          value={code}
          onChange={(event) => onCode(event.target.value)}
        />
        <button type="submit" className="ical-btn ical-btn-primary" disabled={busy}>
          Confirm
        </button>
        <button type="button" className="ical-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </form>
    </div>
  );
}

/** ConfirmationForm is a code input, with an optional "send me one" step. */
function ConfirmationForm({ busy, code, onCode, title, confirmLabel, sendLabel, onSend, onConfirm, onCancel }) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onConfirm();
      }}
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      <p style={{ margin: 0, fontSize: "var(--text-sm)" }}>{title}</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          className="ical-input"
          inputMode="numeric"
          required
          placeholder="6-digit code"
          value={code}
          onChange={(event) => onCode(event.target.value)}
        />
        <button type="submit" className="ical-btn ical-btn-danger" disabled={busy}>
          {confirmLabel}
        </button>
        {onSend ? (
          <button type="button" className="ical-btn" onClick={onSend} disabled={busy}>
            {sendLabel}
          </button>
        ) : null}
        <button type="button" className="ical-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * The zones offered, in the order a customer is likely to look for their own.
 *
 * A short list rather than all six hundred: a select that long is unusable, and
 * the value is an IANA name the server validates, so anyone whose zone is
 * missing can still be served by the stored value showing here.
 */
const TIMEZONES = [
  "Europe/Berlin",
  "Europe/Warsaw",
  "Europe/Prague",
  "Europe/Vienna",
  "Europe/Amsterdam",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/London",
  "Europe/Lisbon",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "UTC",
];

/**
 * TimezoneSection is the zone the customer reads times in.
 *
 * It sits with the password because it is a setting about the account rather
 * than about a project, but it asks for no password: it changes how a page looks
 * to the person looking at it and nothing about what the account may do.
 */
function TimezoneSection({ timezone, busy, onChange }) {
  const current = timezone || "Europe/Berlin";
  // A stored zone outside the list keeps its own option, so opening the screen
  // cannot silently move somebody to Berlin just by rendering.
  const options = TIMEZONES.includes(current) ? TIMEZONES : [current, ...TIMEZONES];

  // The label sits on the select and not the section: two elements sharing an
  // accessible name make the control unreachable by name, which is a test
  // failure and, for anyone using a screen reader, a real one.
  return (
    <section className="ical-card">
      <p style={{ margin: "0 0 4px", fontWeight: 500 }}>Display timezone</p>
      <p style={{ margin: "0 0 12px", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
        Times across the panel are shown in this zone.
      </p>
      <select
        className="ical-select"
        aria-label="Display timezone"
        value={current}
        disabled={busy}
        onChange={(event) => onChange?.(event.target.value)}
      >
        {options.map((zone) => (
          <option key={zone} value={zone}>
            {zone}
          </option>
        ))}
      </select>
    </section>
  );
}
